import { defineStore } from 'pinia';
import { usePreferenceStorage } from '@/composables/use-preference-storage';
import { AvailableResult, BiometryType, Credentials, NativeBiometric } from 'capacitor-native-biometric';
import { computed, ref, watchEffect } from 'vue';
import { alertController, isPlatform } from '@ionic/vue';

const BIOMETRIC_SERVER = 'https://nextcenturymeters.com';

export const useBiometrics = defineStore('Biometrics', () => {
  const { state: userEnabled, waitForInit: waitForEnabledInit } = usePreferenceStorage<boolean>(
    'ncss-biometrics-enabled',
    { default: false }
  );
  const { state: cachedUsername, waitForInit: waitForUsernameInit } =
    usePreferenceStorage<string>('ncss-mobile-username');
  const availableState = ref<AvailableResult>();
  const isAvailable = computed((): boolean => {
    return !!(availableState.value?.isAvailable && cachedUsername.value && userEnabled.value);
  });
  const isFaceID = computed(
    () =>
      availableState.value?.biometryType == BiometryType.NONE ||
      availableState.value?.biometryType == BiometryType.FACE_ID ||
      availableState.value?.biometryType == BiometryType.FACE_AUTHENTICATION
  );

  watchEffect(() => console.log('BIOMETRICS', availableState.value));
  watchEffect(() => console.log('CACHED USERNAME', cachedUsername.value));

  async function init() {
    await waitForEnabledInit();
    await waitForUsernameInit();
    if (isPlatform('hybrid')) {
      availableState.value = await NativeBiometric.isAvailable();
    }
  }

  async function getCredentials(): Promise<Credentials | undefined> {
    if (!isAvailable.value) {
      return;
    }
    const verified = await NativeBiometric.verifyIdentity({
      reason: 'For easy log in',
      title: 'Login',
      maxAttempts: 2,
      useFallback: true,
    })
      .then(() => true)
      .catch(() => false);

    if (!verified) {
      return;
    }

    const credentials = await NativeBiometric.getCredentials({
      server: BIOMETRIC_SERVER,
    });
    return credentials;
  }

  async function setCredentials(username: string, password: string) {
    cachedUsername.value = username;
    if (!isAvailable.value) {
      return;
    }
    await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
    await NativeBiometric.setCredentials({
      username,
      password,
      server: BIOMETRIC_SERVER,
    });
  }

  async function clearCredentials() {
    if (!isAvailable.value) {
      return;
    }
    cachedUsername.value = undefined;
    await NativeBiometric.deleteCredentials({
      server: BIOMETRIC_SERVER,
    });
  }

  async function prompt() {
    const a = await alertController.create({
      header: 'Allow "NCSS" to use Biometric Login?',
      buttons: [
        {
          role: 'cancel',
          text: "Don't Allow",
        },
        {
          role: 'allow',
          text: 'Allow',
        },
      ],
    });
    await a.present();
    const { role } = await a.onDidDismiss();
    userEnabled.value = role === 'allow';
  }

  init();
  return {
    userEnabled,
    isAvailable,
    isFaceID,
    init,
    setCredentials,
    getCredentials,
    clearCredentials,
    cachedUsername,
    prompt,
  };
});
